import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { GooglePayAddress, PaymentMethodData } from './interfaces/payment-method-data.interface';
import { GooglePayButtonAnalytics } from './google-pay-button.analytics';
import { PaymentDetails, PaymentType, RmsBookstoreAddress } from '../../checkout/shop-checkout.interface';
import { StoreState } from '../../../store/store.interface';
import { booleanConverter } from '../../../common/converters';
import { connect } from 'pwa-helpers';
import { createDataCollector, getDataCollector } from '../braintree';
import { createGooglePayInstance } from './thunks/google-pay-instance.thunk';
import { createGooglePaymentClient } from './thunks/google-pay-client.thunk';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getGooglePayInstance, getGooglePaymentClient, isGooglePayReadyUse } from './store/google-pay.selectors';
import { loading } from '../../../common/constants/api-constants';
import { waitForElementsToLoad } from '../../../common/utils/delay-util';

const SKELETON_WRAPPER_CSS_CLASS_SMALL = 'small-size-skeleton';
const SKELETON_WRAPPER_CSS_CLASS_LARGE = 'large-size-skeleton';

/**
 * Configure and load Google Pay button.
 * Saves google pay payment details in redux.
 */
@customElement('mhecomm-google-pay-button')
export default class GooglePayButton extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .small-size-skeleton.loading {
        width: 285px;
      }

      .large-size-skeleton .gpay-button {
        margin-bottom: 0.5rem;
        position: relative;
        top: 50%;
        width: 100%;
      }

      #google-pay-button-container button:focus{
        outline-offset: 2px;
        outline: 2px solid black;
      }
    `,
  ];

  private analytics: GooglePayButtonAnalytics;

  constructor() {
    super();
    this.analytics = new GooglePayButtonAnalytics();
  }
  /**
   * Braintree Client token to initialize Google Pay button.
   */
  @property()
  clientToken = '';

  /**
   * Transaction amount to be used on Google Pay.
   */
  @property({ type: String })
  amount = '0.00';

  /**
   * Currency for Google Pay transaction.
   */
  @property()
  currencyCode = 'USD';

  /**
   * Button color for the Google Pay button
   */
  @property()
  buttonColor: 'black' | 'white' = 'black';

  /**
   * Button type for the Google Pay button
   */
  @property()
  buttonType: 'checkout' | 'pay' = 'checkout';

  /**
   * Button size for the Google Pay button
   */
  @property()
  buttonSize: 'small' | 'large' = 'small';

  /**
   * Margins for the Google Pay button loader.
   */
  @property()
  loaderMargins = 'mt-0';

  /**
   * Flag to indicate the checkout contains shippable items.
   */
  @property({ converter: booleanConverter })
  shippable = false;

  @state()
  isLoading = true;

  /**
   * Google Pay payment details
   */
  @state()
  private paymentDetails: PaymentDetails = {};

  /**
   * @param _changedProperties
   */
  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    await this.setUpGooglePayButton();
  }

  renderComp() {
    return html`
      <div
        id="google-pay-wrapper-div"
        class="loading ${this.buttonSize === 'small' ? SKELETON_WRAPPER_CSS_CLASS_SMALL : SKELETON_WRAPPER_CSS_CLASS_LARGE}"
      >
        ${this.isLoading
          ? html`
            ${loading({
              columns: 1,
              width: '100%',
              height: '2.5rem',
              m: this.loaderMargins
            })} `
          : nothing
        }
        <div id="google-pay-button-container" class="d-none"></div>
      </div>
    `;
  }

  private getButtonContainer() {
    return this.shadowRoot!.querySelector('#google-pay-button-container') as HTMLElement;
  }

  private getWrapperDiv() {
    return this.shadowRoot!.querySelector('#google-pay-wrapper-div') as HTMLElement;
  }

  async setUpGooglePayButton() {
    try {
      await store.dispatch(createGooglePaymentClient({}));
      await store.dispatch(createGooglePayInstance({ clientToken: this.clientToken }));
      const state = store.getState() as StoreState;
      if (isGooglePayReadyUse(state)) {
        const supportsGooglePay = await this.isGooglePaySupported(state);
        if (!supportsGooglePay.result) {
          console.warn(`Current device doesn't support google pay.`);
          this.isLoading = false;
          return;
        }
        this.createGooglePayButton(state);
      }
    } catch (err) {
      console.error(`Failed to configure the google pay: ${(err as Error).message}`);
      this.isLoading = false;
    }
  }

  async isGooglePaySupported(state: StoreState) {
    const paymentClient = getGooglePaymentClient(state);
    try {
      return await paymentClient.isReadyToPay({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: getGooglePayInstance(state).createPaymentDataRequest().allowedPaymentMethods,
      });
    } catch (err) {
      console.error(`Device not supported: ${(err as Error).message}`);
      throw err;
    }
  }

  async createGooglePayButton(state: StoreState) {
    const allowedPaymentMethods = getGooglePayInstance(state).createPaymentDataRequest().allowedPaymentMethods;
    const creditCardPaymentMethod = this.filterOutPaypalAsNotSupportedOnBrowsers(allowedPaymentMethods);
    const button = getGooglePaymentClient(state).createButton({
      buttonRootNode: this.shadowRoot,
      buttonColor: this.buttonColor,
      buttonType: this.buttonType,
      onClick: async (event: Event) => this.handlePayment(event),
      allowedPaymentMethods: creditCardPaymentMethod,
    });
    waitForElementsToLoad(this.shadowRoot!, ['#google-pay-button-container']).then(() => {
      this.getButtonContainer().appendChild(button);
      this.getButtonContainer().classList.remove('d-none');
      this.getWrapperDiv().classList.remove('loading');
      this.isLoading = false;
    });
  }

  async handlePayment(event: Event) {
    event.preventDefault();
    const state = store.getState() as StoreState;
    this.analytics.startEvent(state, this, this.amount);
    try {
      const paymentData = await getGooglePaymentClient(state).loadPaymentData(this.getPaymentDataRequest(state));
      getGooglePayInstance(state).parseResponse(paymentData, async (err, result) => {
        if (err) {
          console.error(`Error to complete the google pay transaction callback: ${err.message}`);
          return;
        }

        this.paymentDetails = await this.buildPaymentDetails(result);
        const billingAddress = this.extractBillingAddress(paymentData.paymentMethodData);
        const shippingAddress = this.extractShippingAddress(paymentData);
        this.analytics.checkoutInfoCollected(this, this.paymentDetails, billingAddress, shippingAddress);
      });
    } catch (err) {
      console.error(`Error to complete the google pay transaction result ${(err as Error).message}`);
    }
  }

  getPaymentDataRequest(state: StoreState) {
    var paymentDataRequest = getGooglePayInstance(state).createPaymentDataRequest({
      transactionInfo: {
        currencyCode: this.currencyCode,
        totalPriceStatus: 'ESTIMATED',
        totalPrice: this.amount,
      },
    });

    if (this.shippable) {
      paymentDataRequest.shippingAddressRequired = true;
      paymentDataRequest.shippingAddressParameters = {
        phoneNumberRequired: true,
      };
    }
    paymentDataRequest.allowedPaymentMethods = this.filterOutPaypalAsNotSupportedOnBrowsers(
      paymentDataRequest.allowedPaymentMethods
    );
    var cardPaymentMethod = paymentDataRequest.allowedPaymentMethods[0];
    cardPaymentMethod.parameters.billingAddressRequired = true;
    cardPaymentMethod.parameters.billingAddressParameters = {
      format: 'FULL',
      phoneNumberRequired: true,
    };

    return paymentDataRequest;
  }

  private filterOutPaypalAsNotSupportedOnBrowsers(allowedPaymentMethods: Array<any>) {
    return allowedPaymentMethods.filter((paymentMethod) => 'CARD' === paymentMethod.type);
  }

  async buildPaymentDetails(result: any) {
    await store.dispatch(
      createDataCollector({
        clientToken: this.clientToken,
      })
    );
    return {
      paymentType: PaymentType.BRAINTREE_GOOGLE_PAY,
      paymentNonce: result.nonce,
      thirdPartyDetails: {
        cardType: result.details.cardType,
        lastDigits: result.details.lastFour,
      },
      deviceData: getDataCollector(store.getState() as StoreState).deviceData,
    };
  }

  extractBillingAddress(paymentData: PaymentMethodData): RmsBookstoreAddress {
    return this.extractGoogleAddress(paymentData.info.billingAddress);
  }

  extractShippingAddress(paymentData: PaymentMethodData): RmsBookstoreAddress | undefined {
    if (!this.shippable) {
      return undefined;
    }
    return this.extractGoogleAddress(paymentData.shippingAddress);
  }

  extractGoogleAddress(address: GooglePayAddress) {
    const name = address.name;
    const indexEndFirstName = name.includes(' ') ? name.indexOf(' ') : name.length;
    const firstName = name.substring(0, indexEndFirstName);
    const lastName = name.substring(indexEndFirstName + 1);

    return {
      firstName: firstName,
      lastName: lastName,
      address1: address.address1,
      address2: address.address2,
      city: address.locality,
      country: address.countryCode,
      zipCode: address.postalCode,
      state: address.administrativeArea,
      phoneNumber: address.phoneNumber ?? '',
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-google-pay-button': GooglePayButton;
  }
}
